import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuthStore } from '../../store/authStore';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Plus, Edit, Trash2, Eye, EyeOff, Calendar, Clock, 
  FileText, Tag, Folder, Save, X, ExternalLink, Loader2, Upload
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Simple Blog Image Upload - avoids nested dialog issues
function BlogImageUpload({ image, onImageChange }) {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setIsUploading(true);
    setUploadProgress(0);
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('No autenticado');
        return;
      }

      // Get signature from backend - use 'articles/' folder which is allowed
      const sigResponse = await axios.get(`${API_URL}/api/cloudinary/signature`, {
        params: { folder: 'articles/', resource_type: 'image' },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const { signature, timestamp, cloud_name, api_key, folder: signedFolder } = sigResponse.data;

      // Prepare form data for Cloudinary upload
      const formData = new FormData();
      formData.append('file', file);
      formData.append('api_key', api_key);
      formData.append('timestamp', timestamp);
      formData.append('signature', signature);
      formData.append('folder', signedFolder);

      // Upload directly to Cloudinary
      const uploadResponse = await axios.post(
        `https://api.cloudinary.com/v1_1/${cloud_name}/image/upload`,
        formData,
        {
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(progress);
          }
        }
      );

      onImageChange(uploadResponse.data.secure_url);
      toast.success('Imagen subida');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Error al subir imagen');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const removeImage = () => {
    onImageChange('');
  };

  return (
    <div className="space-y-2">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />
      
      {image ? (
        <div className="relative rounded-lg overflow-hidden border">
          <img src={image} alt="Featured" className="w-full h-40 object-cover" />
          <button
            onClick={removeImage}
            className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
            type="button"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <div 
          onClick={() => !isUploading && fileInputRef.current?.click()}
          className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-gold transition-colors ${isUploading ? 'opacity-50 cursor-wait' : ''}`}
        >
          {isUploading ? (
            <div className="space-y-2">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-gold" style={{ color: '#D4A574' }} />
              <p className="text-sm text-secondary">Subiendo... {uploadProgress}%</p>
            </div>
          ) : (
            <>
              <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
              <p className="text-sm text-secondary">Haz clic para subir imagen</p>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// Rich text editor placeholder - using textarea for now
// Can be replaced with TinyMCE, Quill, or similar
function RichTextEditor({ value, onChange, placeholder }) {
  return (
    <Textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="min-h-[200px] font-mono text-sm"
    />
  );
}

export default function BlogTab() {
  const { t, i18n } = useTranslation();
  const { getAuthHeaders } = useAuthStore();
  const language = i18n.language;
  
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingArticle, setEditingArticle] = useState(null);
  const [showArticleDialog, setShowArticleDialog] = useState(false);
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Article form state
  const [articleForm, setArticleForm] = useState({
    title: { es: '', en: '', it: '' },
    content: { es: '', en: '', it: '' },
    excerpt: { es: '', en: '', it: '' },
    meta_description: { es: '', en: '', it: '' },
    keywords: { es: [], en: [], it: [] },
    featured_image: '',
    category_id: '',
    tags: [],
    status: 'draft'
  });
  
  // Category form state
  const [categoryForm, setCategoryForm] = useState({
    name: { es: '', en: '', it: '' },
    slug: '',
    description: { es: '', en: '', it: '' }
  });
  
  const [keywordsInput, setKeywordsInput] = useState({ es: '', en: '', it: '' });
  const [tagsInput, setTagsInput] = useState('');
  const [contentLang, setContentLang] = useState('es');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [articlesRes, categoriesRes] = await Promise.all([
        axios.get(`${API_URL}/api/blog/admin/articles`, {
          headers: getAuthHeaders(),
          params: { limit: 100 }
        }),
        axios.get(`${API_URL}/api/blog/categories`)
      ]);
      
      setArticles(articlesRes.data.articles || []);
      setCategories(categoriesRes.data || []);
    } catch (error) {
      console.error('Error fetching blog data:', error);
      toast.error('Error al cargar los datos del blog');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const resetArticleForm = () => {
    setArticleForm({
      title: { es: '', en: '', it: '' },
      content: { es: '', en: '', it: '' },
      excerpt: { es: '', en: '', it: '' },
      meta_description: { es: '', en: '', it: '' },
      keywords: { es: [], en: [], it: [] },
      featured_image: '',
      category_id: categories[0]?.id || '',
      tags: [],
      status: 'draft'
    });
    setKeywordsInput({ es: '', en: '', it: '' });
    setTagsInput('');
    setEditingArticle(null);
  };

  const openNewArticle = () => {
    resetArticleForm();
    setShowArticleDialog(true);
  };

  const openEditArticle = (article) => {
    setEditingArticle(article);
    setArticleForm({
      title: article.title || { es: '', en: '', it: '' },
      content: article.content || { es: '', en: '', it: '' },
      excerpt: article.excerpt || { es: '', en: '', it: '' },
      meta_description: article.meta_description || { es: '', en: '', it: '' },
      keywords: article.keywords || { es: [], en: [], it: [] },
      featured_image: article.featured_image || '',
      category_id: article.category_id || '',
      tags: article.tags || [],
      status: article.status || 'draft'
    });
    setKeywordsInput({
      es: (article.keywords?.es || []).join(', '),
      en: (article.keywords?.en || []).join(', '),
      it: (article.keywords?.it || []).join(', ')
    });
    setTagsInput((article.tags || []).join(', '));
    setShowArticleDialog(true);
  };

  const handleSaveArticle = async () => {
    if (!articleForm.title.es && !articleForm.title.en && !articleForm.title.it) {
      toast.error('Se requiere al menos un título');
      return;
    }
    
    if (!articleForm.category_id) {
      toast.error('Selecciona una categoría');
      return;
    }
    
    setSaving(true);
    try {
      // Parse keywords and tags
      const keywords = {
        es: keywordsInput.es ? keywordsInput.es.split(',').map(k => k.trim()).filter(Boolean) : [],
        en: keywordsInput.en ? keywordsInput.en.split(',').map(k => k.trim()).filter(Boolean) : [],
        it: keywordsInput.it ? keywordsInput.it.split(',').map(k => k.trim()).filter(Boolean) : []
      };
      const tags = tagsInput ? tagsInput.split(',').map(t => t.trim()).filter(Boolean) : [];
      
      const payload = {
        ...articleForm,
        keywords,
        tags
      };
      
      if (editingArticle) {
        await axios.put(
          `${API_URL}/api/blog/admin/articles/${editingArticle.id}`,
          payload,
          { headers: getAuthHeaders() }
        );
        toast.success('Artículo actualizado');
      } else {
        await axios.post(
          `${API_URL}/api/blog/admin/articles`,
          payload,
          { headers: getAuthHeaders() }
        );
        toast.success('Artículo creado');
      }
      
      setShowArticleDialog(false);
      resetArticleForm();
      fetchData();
    } catch (error) {
      console.error('Error saving article:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar el artículo');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteArticle = async (articleId) => {
    if (!window.confirm('¿Seguro que quieres eliminar este artículo?')) return;
    
    try {
      await axios.delete(
        `${API_URL}/api/blog/admin/articles/${articleId}`,
        { headers: getAuthHeaders() }
      );
      toast.success('Artículo eliminado');
      fetchData();
    } catch (error) {
      console.error('Error deleting article:', error);
      toast.error('Error al eliminar el artículo');
    }
  };

  const handleToggleStatus = async (article) => {
    const newStatus = article.status === 'published' ? 'draft' : 'published';
    try {
      await axios.put(
        `${API_URL}/api/blog/admin/articles/${article.id}`,
        { status: newStatus },
        { headers: getAuthHeaders() }
      );
      toast.success(newStatus === 'published' ? 'Artículo publicado' : 'Artículo despublicado');
      fetchData();
    } catch (error) {
      console.error('Error toggling status:', error);
      toast.error('Error al cambiar el estado');
    }
  };

  const handleSaveCategory = async () => {
    if (!categoryForm.name.es && !categoryForm.name.en && !categoryForm.name.it) {
      toast.error('Se requiere al menos un nombre');
      return;
    }
    if (!categoryForm.slug) {
      toast.error('Se requiere un slug');
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(
        `${API_URL}/api/blog/admin/categories`,
        categoryForm,
        { headers: getAuthHeaders() }
      );
      toast.success('Categoría creada');
      setShowCategoryDialog(false);
      setCategoryForm({ name: { es: '', en: '', it: '' }, slug: '', description: { es: '', en: '', it: '' } });
      fetchData();
    } catch (error) {
      console.error('Error saving category:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar la categoría');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('¿Seguro que quieres eliminar esta categoría?')) return;
    
    try {
      await axios.delete(
        `${API_URL}/api/blog/admin/categories/${categoryId}`,
        { headers: getAuthHeaders() }
      );
      toast.success('Categoría eliminada');
      fetchData();
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error(error.response?.data?.detail || 'Error al eliminar la categoría');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getCategoryName = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat?.name?.es || cat?.name?.en || '-';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-gold" style={{ color: '#D4A574' }} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="blog-admin-tab">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl font-bold text-primary">Blog</h2>
          <p className="text-sm text-secondary">{articles.length} artículos, {categories.length} categorías</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setShowCategoryDialog(true)}
            className="flex items-center gap-2"
          >
            <Folder className="h-4 w-4" />
            Nueva Categoría
          </Button>
          <Button
            onClick={openNewArticle}
            className="flex items-center gap-2 text-white"
            style={{ backgroundColor: '#D4A574' }}
          >
            <Plus className="h-4 w-4" />
            Nuevo Artículo
          </Button>
        </div>
      </div>

      {/* Articles Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-medium text-secondary">Título</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-secondary">Categoría</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-secondary">Estado</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-secondary">Fecha</th>
                <th className="text-right px-4 py-3 text-sm font-medium text-secondary">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {articles.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-secondary">
                    No hay artículos. Crea el primero.
                  </td>
                </tr>
              ) : (
                articles.map(article => (
                  <tr key={article.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {article.featured_image ? (
                          <img 
                            src={article.featured_image} 
                            alt="" 
                            className="w-12 h-8 object-cover rounded"
                          />
                        ) : (
                          <div className="w-12 h-8 bg-gray-100 rounded flex items-center justify-center">
                            <FileText className="h-4 w-4 text-gray-400" />
                          </div>
                        )}
                        <div>
                          <p className="font-medium text-primary line-clamp-1">
                            {article.title?.es || article.title?.en || article.title?.it || 'Sin título'}
                          </p>
                          <p className="text-xs text-secondary">
                            {article.reading_time} min · {article.author_name}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-secondary">
                        {getCategoryName(article.category_id)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span 
                        className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                          article.status === 'published' 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {article.status === 'published' ? 'Publicado' : 'Borrador'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-secondary">
                      {formatDate(article.published_at || article.created_at)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleToggleStatus(article)}
                          className="p-1.5 hover:bg-gray-100 rounded"
                          title={article.status === 'published' ? 'Despublicar' : 'Publicar'}
                        >
                          {article.status === 'published' ? (
                            <EyeOff className="h-4 w-4 text-gray-500" />
                          ) : (
                            <Eye className="h-4 w-4 text-green-600" />
                          )}
                        </button>
                        <button
                          onClick={() => openEditArticle(article)}
                          className="p-1.5 hover:bg-gray-100 rounded"
                          title="Editar"
                        >
                          <Edit className="h-4 w-4 text-blue-600" />
                        </button>
                        <button
                          onClick={() => handleDeleteArticle(article.id)}
                          className="p-1.5 hover:bg-gray-100 rounded"
                          title="Eliminar"
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </button>
                        {article.status === 'published' && (
                          <a
                            href={`/blog/${article.slug?.es || article.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-1.5 hover:bg-gray-100 rounded"
                            title="Ver"
                          >
                            <ExternalLink className="h-4 w-4 text-gray-500" />
                          </a>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Categories Section */}
      <div className="bg-white rounded-lg shadow p-4">
        <h3 className="font-medium text-primary mb-3 flex items-center gap-2">
          <Folder className="h-4 w-4" />
          Categorías
        </h3>
        <div className="flex flex-wrap gap-2">
          {categories.map(cat => (
            <div 
              key={cat.id}
              className="inline-flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-full text-sm"
            >
              <span>{cat.name?.es || cat.name?.en}</span>
              {!cat.is_predefined && (
                <button
                  onClick={() => handleDeleteCategory(cat.id)}
                  className="text-red-500 hover:text-red-700"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
              {cat.is_predefined && (
                <span className="text-xs text-secondary">(predefinida)</span>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Article Dialog */}
      <Dialog open={showArticleDialog} onOpenChange={setShowArticleDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingArticle ? 'Editar Artículo' : 'Nuevo Artículo'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Language tabs for content */}
            <Tabs value={contentLang} onValueChange={setContentLang}>
              <TabsList className="mb-4">
                <TabsTrigger value="es">Español</TabsTrigger>
                <TabsTrigger value="en">English</TabsTrigger>
                <TabsTrigger value="it">Italiano</TabsTrigger>
              </TabsList>
              
              {['es', 'en', 'it'].map(lang => (
                <TabsContent key={lang} value={lang} className="space-y-4">
                  <div>
                    <Label>Título ({lang.toUpperCase()})</Label>
                    <Input
                      value={articleForm.title[lang]}
                      onChange={(e) => setArticleForm(prev => ({
                        ...prev,
                        title: { ...prev.title, [lang]: e.target.value }
                      }))}
                      placeholder={`Título del artículo en ${lang === 'es' ? 'español' : lang === 'en' ? 'inglés' : 'italiano'}`}
                    />
                  </div>
                  
                  <div>
                    <Label>Extracto ({lang.toUpperCase()}) - Máx 200 caracteres</Label>
                    <Textarea
                      value={articleForm.excerpt[lang]}
                      onChange={(e) => setArticleForm(prev => ({
                        ...prev,
                        excerpt: { ...prev.excerpt, [lang]: e.target.value.slice(0, 200) }
                      }))}
                      placeholder="Breve resumen del artículo"
                      className="h-20"
                    />
                  </div>
                  
                  <div>
                    <Label>Contenido ({lang.toUpperCase()}) - Soporta HTML</Label>
                    <RichTextEditor
                      value={articleForm.content[lang]}
                      onChange={(val) => setArticleForm(prev => ({
                        ...prev,
                        content: { ...prev.content, [lang]: val }
                      }))}
                      placeholder="Contenido del artículo (puedes usar HTML: <h2>, <p>, <ul>, <strong>, etc.)"
                    />
                  </div>
                  
                  <div>
                    <Label>Meta Description ({lang.toUpperCase()}) - SEO</Label>
                    <Textarea
                      value={articleForm.meta_description[lang]}
                      onChange={(e) => setArticleForm(prev => ({
                        ...prev,
                        meta_description: { ...prev.meta_description, [lang]: e.target.value.slice(0, 160) }
                      }))}
                      placeholder="Descripción para motores de búsqueda (máx 160 caracteres)"
                      className="h-16"
                    />
                    <p className="text-xs text-secondary mt-1">
                      {articleForm.meta_description[lang]?.length || 0}/160 caracteres
                    </p>
                  </div>
                  
                  <div>
                    <Label>Keywords ({lang.toUpperCase()}) - Separadas por coma</Label>
                    <Input
                      value={keywordsInput[lang]}
                      onChange={(e) => setKeywordsInput(prev => ({ ...prev, [lang]: e.target.value }))}
                      placeholder="tantra, meditación, bienestar, yoga"
                    />
                  </div>
                </TabsContent>
              ))}
            </Tabs>
            
            {/* Common fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t">
              <div>
                <Label>Categoría</Label>
                <Select 
                  value={articleForm.category_id} 
                  onValueChange={(val) => setArticleForm(prev => ({ ...prev, category_id: val }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona categoría" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.name?.es || cat.name?.en}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Estado</Label>
                <Select 
                  value={articleForm.status} 
                  onValueChange={(val) => setArticleForm(prev => ({ ...prev, status: val }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Borrador</SelectItem>
                    <SelectItem value="published">Publicado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label>Tags (separados por coma)</Label>
              <Input
                value={tagsInput}
                onChange={(e) => setTagsInput(e.target.value)}
                placeholder="principiantes, parejas, técnicas"
              />
            </div>
            
            <div>
              <Label>Imagen destacada</Label>
              <BlogImageUpload
                image={articleForm.featured_image}
                onImageChange={(url) => setArticleForm(prev => ({ ...prev, featured_image: url }))}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowArticleDialog(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSaveArticle} 
              disabled={saving}
              className="text-white"
              style={{ backgroundColor: '#D4A574' }}
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Category Dialog */}
      <Dialog open={showCategoryDialog} onOpenChange={setShowCategoryDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nueva Categoría</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Nombre (Español) *</Label>
              <Input
                value={categoryForm.name.es}
                onChange={(e) => setCategoryForm(prev => ({
                  ...prev,
                  name: { ...prev.name, es: e.target.value }
                }))}
                placeholder="Nombre en español"
              />
            </div>
            
            <div>
              <Label>Nombre (English)</Label>
              <Input
                value={categoryForm.name.en}
                onChange={(e) => setCategoryForm(prev => ({
                  ...prev,
                  name: { ...prev.name, en: e.target.value }
                }))}
                placeholder="Name in English"
              />
            </div>
            
            <div>
              <Label>Nombre (Italiano)</Label>
              <Input
                value={categoryForm.name.it}
                onChange={(e) => setCategoryForm(prev => ({
                  ...prev,
                  name: { ...prev.name, it: e.target.value }
                }))}
                placeholder="Nome in italiano"
              />
            </div>
            
            <div>
              <Label>Slug (URL) *</Label>
              <Input
                value={categoryForm.slug}
                onChange={(e) => setCategoryForm(prev => ({
                  ...prev,
                  slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-')
                }))}
                placeholder="nombre-categoria"
              />
              <p className="text-xs text-secondary mt-1">
                Solo letras minúsculas, números y guiones
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCategoryDialog(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSaveCategory} 
              disabled={saving}
              className="text-white"
              style={{ backgroundColor: '#D4A574' }}
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Crear Categoría
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
